import axios from "axios";
import { useMemo } from "react";
import { BASE_URL } from "../../src/config-global";



function filterParamsToUrl(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value);
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
}


export default function useAPI() {
    const authConfig = {
        headers: {
            "Content-Type": "application/json",
        },
    };
    const config = useMemo(
        () => () => ({
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }),
        []
      );
    const fileConfig = useMemo(
        () => () => ({
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }),
        []
      );

    return {
        getDocument: async (image_url) => {
            const response = await axios.get(`${image_url}`,
                {
                    ...config(),
                    responseType: 'blob',
                },
            );
            return response;
        },

        signIn: async (username, password) => {
            const response = await axios.post(
                `${BASE_URL}/api/token/`,
                {
                    username,
                    password,
                },
            );
            // 202 înseamnă că este necesară verificarea în doi pași și nu vin token-uri.
            return { ...response.data, status: response.status };
        },

        verifyTwoFactor: async (ephemeralToken, code) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/token/two-factor/verify/`,
                {
                    ephemeral_token: ephemeralToken,
                    code,
                },
                authConfig,
            );
            return data;
        },

        resendTwoFactorCode: async (ephemeralToken) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/token/two-factor/resend/`,
                { ephemeral_token: ephemeralToken },
                authConfig,
            );
            return data;
        },

        enableTwoFactor: async (currentPassword) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/two-factor/enable/`,
                { current_password: currentPassword },
                config(),
            );
            return data;
        },

        confirmEnableTwoFactor: async (code) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/two-factor/enable/confirm/`,
                { code },
                config(),
            );
            return data;
        },

        disableTwoFactor: async (currentPassword) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/two-factor/disable/`,
                { current_password: currentPassword },
                config(),
            );
            return data;
        },

        logout: async (refreshToken) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/logout/`,
                { refreshToken },
                config(),
            );
            return data;
        },

        getCurrentUser: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/get-current-user/`,
                config(),
            );
            return data;
        },

        changePassword: async (passwordData) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/change-password/`,
                passwordData,
                config(),
            );
            return data;
        },

        getComplaintFormData: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/complaint-form/form-data/`,
                config(),
            );
            return data;
        },

        getComplaintFornActions: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/complaint-form/form-actions/`,
                config(),
            );
            return data;
        },

        registerComplaintForm: async (formData) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/register/`,
                formData,
                fileConfig(),
            );
            return data;
        },

        validateComplaintFormEmail: async (code, complaintId) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/validate-email/`,
                code,
                fileConfig(),
            );
            return data;
        },

        getComplaintFormList: async (params = {}) => {
            const url = `${BASE_URL}/api/complaint-form/list/${filterParamsToUrl(params)}`;
            const { data } = await axios.get(
                url,
                config(),
            );
            return data;
        },

        getRedirectedComplaintFormList: async (params = {}) => {
            const url = `${BASE_URL}/api/complaint-form/list/redirected/${filterParamsToUrl(params)}`;
            const { data } = await axios.get(
                url,
                config(),
            );
            return data;
        },

        getComplaintFormDetails: async (id) => {
            const { data } = await axios.get(
                `${BASE_URL}/api/complaint-form/details/${id}/`,
                config(),
            );
            return data;
        },

        updateComplaintFormStatus: async (id, data) => {
            const { data: response } = await axios.patch(
                `${BASE_URL}/api/complaint-form/change-status/${id}/`,
                data,
                config(),
            );
            return response;
        },

        registerRedirectComplaintForm: async (id, data) => {
            const { response } = await axios.post(
                `${BASE_URL}/api/complaint-form/create-redirect/${id}/`,
                data,
            );
            return response;
        },

        registerResolvedComplaintForm: async (id, formData) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/create-resolved/${id}/`,
                formData,
                fileConfig(),
            );
            return data;
        },
        updateResolvedMessage: async (resolvedId, updateData) => {
            // Accept either a string (legacy) or an object with all fields
            const payload = typeof updateData === "string"
                ? { resolution_details: updateData }
                : updateData;
            const { data } = await axios.patch(
                `${BASE_URL}/api/complaint-form/update-resolved/${resolvedId}/`,
                payload,
                config(),
            );
            return data;
        },
        addResolvedFile: async (resolvedId, file) => {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/add-file/${resolvedId}/`,
                formData,
                fileConfig(),
            );
            return data;
        },
        deleteResolvedFile: async (fileId, fileUrl) => {
            const data = await axios.delete(
                `${BASE_URL}/api/complaint-form/delete-file/${fileId}/`,
                config()
            );
            return data
        },
        
        userCheckComplaintFormEmail: async (formData) => {
            // Verificare Status Sesizare - Trimitere email
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/user-check-form-email/`,
                formData,
            );
            return data;
        },
        userCheckComplaintFormCode: async (formData) => {
            // Verificare Status Sesizare - Trimitere cod de la userCheckComplaintFormEmail
            const { data } = await axios.post(
                `${BASE_URL}/api/complaint-form/user-check-form-code/`,
                formData,
            );
            return data;
        },

        getCarouselContent: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/content/carousel-content/`,
                config(),
            );
            return data;
        },

        getCarouselThumbnails: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/content/carousel-thumbnails/`,
                config(),
            );
            return data;
        },

        getResourcesContent: async (params = {}) => {
            const url = `${BASE_URL}/api/content/page-resources/${filterParamsToUrl(params)}`;
            const { data } = await axios.get(url, config());
            return data;
        },

        getLawContent: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/content/law-content/`,
                config(),
            );
            return data;
        },

        getNewsContent: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/content/news-content/`,
                config(),
            );
            return data;
        },

        getGeneralStatistics: async () => {
            const { data } = await axios.get(
                `${BASE_URL}/api/complaint-form/general-statistics/`,
                config(),
            );
            return data;
        },

        getFeedbackForm: async (token) => {
            const { data } = await axios.get(
                `${BASE_URL}/api/forms/${token}/`,
                authConfig,
            );
            return data;
        },

        registerFeedbackForm: async (token, formData) => {
            const { data } = await axios.post(
                `${BASE_URL}/api/forms/${token}/`,
                formData,
                authConfig,
            );
            return data;
        },
    }
}